import {
  Badge,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Fragment, useEffect, useMemo, useState } from "react";
import {
  AddCircleOutline,
  CheckBoxOutlineBlankOutlined,
  CheckBoxOutlined,
  RemoveCircleTwoTone,
} from "@mui/icons-material";
import { useTheme } from "@emotion/react";
import { AddOrRemoveFormConfig } from "../../config/forms/AddOrRemovePerson-config";
import { END_POINT } from "../../utils/End-points";
import ApiClient from "../../apis/Api-client";
import { format } from "date-fns";
import {
  errorToster,
  successToster,
  warnToster,
} from "../../shared/toster/Toster";
import { Helper } from "../../utils/Helpers";
import {
  selectTriggered,
  setTriggered,
} from "../../core/store/redux/slices/DashboardApi-slice";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import ConfirmationDlgBox from "../common-components/confirmation-popup";
import ReactWebcam from "../arrival/reactWebcam/React-webcam";
import IconConstants from "../../utils/IconConstants";
import { useTranslation } from "react-i18next";
import { Can, FEATURES, ACTIONS } from "../../core/permissions";
const {
  getBasePriceFromFomNetRent,
  getNetRetFromBasePrice,
  debounce,
  getLocalTimeFromUtc,
  consolidateTaxRatesSlabs,
  getTaxPercentForAmount,
  getTaxBreakdown,
} = new Helper();

const AddOrRemovePerson = ({ modalName, data, handleClose }) => {
  const { api } = ApiClient();
  const isTriggered = useSelector(selectTriggered);
  const dispatch = useDispatch();
  const themeStyles = useTheme();
  const { t } = useTranslation();
  let primaryColor = themeStyles.palette.primary.main;
  let contrastText = themeStyles.palette.primary.contrastText;
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [confirmationDlg, setConfirmationDlg] = useState(false);
  const [confirmationLabel, setConfirmationLabel] = useState("");
  const [formData, setFormData] = useState({});
  const isRemovePerson = modalName === "Remove Person";
  const {
    occupancy_key,
    person_base_price,
    expected_checkout_date,
    room_no,
    pax,
    no_of_adults,
    no_of_children,
    no_of_extra_persons,
    mobile,
    total_occupancy_tax_rate,
    rate_plan_id
  } = data;

  const [extraAdultPrice, setExtraAdultPrice] = useState(person_base_price);
  const [isLoading, setIsLoading] = useState(rate_plan_id && modalName === "Add Person" ? true : false);
  const [openCamera, setOpenCamera] = useState(false);
  const [capturedImage, setCapturedImage] = useState([]);
  const [defaultCamImage, setDefaultCamImage] = useState([]);
  const [hiddenCamImage, setHiddenCamImage] = useState([]);
  const [taxRates, setTaxRates] = useState([]);
  const [personToCheckout, setPersonToCheckout] = useState(null);
  const methods = useForm({
    resolver: yupResolver(AddOrRemoveFormConfig),
    defaultValues: {
      inc_of_tax: data.inc_of_tax === true || data.inc_of_tax === 1 || data.inc_of_tax === '1',
      extra_persons: [{ addon_price: extraAdultPrice }],
    },
  });

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors },
    reset,
    getValues,
    watch,
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "extra_persons",
  });

  const { inc_of_tax, extra_persons } = watch();

  useEffect(() => {
    occupancy_key && getExtraPersonData();
  }, [occupancy_key]);

  useEffect(() => {
    if (rate_plan_id && modalName === "Add Person") {
      getRatePlanPrice();
    }
  }, [rate_plan_id]);

  useEffect(() => {
    if (modalName === "Add Person" || modalName === "Remove Person") {
      getTaxRateSlabs();
    }
  }, [modalName]);

  const getTaxRateSlabs = async () => {
    try {
      const response = await api.get(END_POINT.TAX.GET_TAX_RATE_SLABS);
      setTaxRates(response?.data?.data || []);
    } catch (error) {
      console.error("Failed to fetch tax rate slabs:", error);
    }
  };

  const getRatePlanPrice = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(END_POINT.RATE_PLAN.GET_ALL);
      const ratePlans = response?.data?.data || [];
      const currentRatePlan = ratePlans.find(rp => rp.rate_plan_id === rate_plan_id);
      if (currentRatePlan) {
        const price = Number(currentRatePlan.extra_adult_price) || 0;
        setExtraAdultPrice(price);
        
        // Update the form values if it's the first empty row
        const currentPersons = getValues("extra_persons");
        if (currentPersons.length === 1 && !currentPersons[0].addon_name) {
          reset({
            ...getValues(),
            extra_persons: [{ addon_price: price }]
          });
        }
      }
    } catch (error) {
      console.error("Failed to fetch rate plan price:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getExtraPersonData = async () => {
    try {
      const response = await api.post(
        END_POINT.ADDON.GET_EXTRA_PERSON_HISTORY,
        { is_checkout_date_null: true },
        {},
        { occupancy_key: occupancy_key }
      );

      const { extra_persons, images } = response?.data?.data ?? {};

      if (isRemovePerson) {
        if (!extra_persons || !Array.isArray(extra_persons) || extra_persons.length === 0) {
          console.warn("No valid extra person data found");
          reset({ ...getValues(), extra_persons: [] });
          return;
        }

        const activeExtraPersons = extra_persons.filter(
          (person) => !person.actual_checkout_date
        );

        if (activeExtraPersons.length === 0) {
          console.warn("No active extra person data found");
          reset({ ...getValues(), extra_persons: [] });
          return;
        }

        const arr = activeExtraPersons.map((v) => {
          const addonTaxRate = v.addon_taxes?.reduce((sum, tax) => sum + Number(tax.tax_rate), 0);
          return {
            addon_key: v.addon_key,
            addon_name: v.addon_name,
            addon_price: inc_of_tax
              ? getNetRetFromBasePrice(Number(v.addon_price), 1, addonTaxRate)
              : Number(v.addon_price),
            addon_id: v.addon_id,
            actual_checkout_date: format(new Date(), "yyyy-MM-dd HH:mm"),
            tax_rate: addonTaxRate
          };
        });

        reset({
          ...getValues(),
          extra_persons: arr,
        });
        setPersonToCheckout(null);
        setDefaultCamImage(images);
      } else {
        setHiddenCamImage(images?.length ? images?.map((v) => v?.key) : "");
      }
    } catch (error) {
      console.error("Failed to fetch extra person data:", error);
    }
  };

  const handleCloseModal = () => {
    handleClose();
    setTimeout(() => {
      reset({ extra_persons: [{}] });
      setPersonToCheckout(null);
    }, 500);
  };

  const hasEmptyFields = (obj) => {
    const fieldsToCheck = ["addon_name", "addon_price"];

    return (
      obj &&
      typeof obj === "object" &&
      fieldsToCheck.some(
        (field) =>
          obj[field] === null || obj[field] === "" || obj[field] === undefined
      )
    );
  };

  const formFields = (countInd) => {
    return {
      loopFields: [
        {
          label: "Name",
          name: `extra_persons.${countInd}.addon_name`,
          type: "text",
          size: "small",
          width: { xs: "100%", sm: "49%" },
          maxLength: 25,
          disable: isRemovePerson,
        },
        {
          label: "Rent",
          name: `extra_persons.${countInd}.addon_price`,
          type: "number",
          size: "small",
          width: { xs: "100%", sm: "49%" },
          disable: isRemovePerson,
          inputAdornment: !!getValues(
            `extra_persons.${countInd}.addon_price`
          ) && (
            <InputAdornment sx={{ p: 0 }}>
              <Typography
                sx={{
                  fontSize: { sm: "1.3vw", md: "0.833vw" },
                  color: "#AAAAAA",
                }}
              >
                {t("common_components.extra_person_popup.per_day")}
              </Typography>
            </InputAdornment>
          ),
        },
      ],
      commonFields: [
        {
          label: "Inc Of Tax",
          type: "checkbox",
          name: "inc_of_tax",
          disable: true,
          width: 1,
        },
      ],
    };
  };

  const renderField = (formField, field, error, countInd) => {
    const commonProps = {
      ...field,
      name: formField.name,
      size: formField.size,
      type: formField.type,
      label: t(
        `common_components.extra_person_popup.${formField.label.toLowerCase()}`
      ),
      autoComplete: "off",
      error: !!error?.message,
      helperText: error
        ? t(
            `common_components.extra_person_popup.alert.${error?.message
              .toLowerCase()
              .replace(/\s+/g, "_")}`
          )
        : "",
      disabled: formField?.disable,
      InputProps: {
        endAdornment: formField?.inputAdornment,
        sx: {
          fontSize: { xs: "4.1vw", sm: "1.619vw", md: "1.111vw" },
          // color: "#AAAAAA",
          fontWeight: "400",
        },
      },
      InputLabelProps: {
        sx: {
          fontSize: { xs: "4.1vw", sm: "1.619vw", md: "1.111vw" },
          fontWeight: 500,
          color: "#AAAAAA",
        },
      },
      FormHelperTextProps: {
        sx: { fontSize: { xs: "3.08vw", sm: "1.333vw", md: "0.833vw" } },
      },
    };

    const handleInput = (e) => {
      const { value } = e.target;
      e.target.value = formField.name.includes("addon_price")
        ? Number(value) === 0
          ? null
          : value.replace(/[^0-9]/g, "")
        : formField.type === "text"
        ? value.replace(/[0-9]/g, "").replace(/^\s+/, "")
        : value.replace(/^\s+/, "");
    };

    switch (formField?.type) {
      case "text":
      case "number":
        return (
          <TextField
            {...commonProps}
            value={watch(formField.name) || ""}
            onWheel={(e) => e.target.blur()}
            // onKeyDown={(e) => handleKeyEvents(e, formField?.name)}
            // onKeyUp={(e) => handleKeyEvents(e, formField?.name)}
            onInput={handleInput}
            // onChange={(e) => handleChange(e, countInd)}
            inputProps={{
              max: formField?.max,
              min: 0,
              maxLength: formField.maxLength || "",
            }}
            className={
              formField.name.includes("no_of_children") ||
              formField.name.includes("no_of_adults")
                ? ""
                : "NoSpinnerTextField no-spinner"
            }
          />
        );
      case "checkbox":
        return (
          <FormControlLabel
            {...field}
            sx={{
              margin: "0 0 0 auto",
              "& .MuiFormControlLabel-label": {
                fontSize: { sm: "1.3vw", md: "0.833vw" },
                color: "#5A5A5A",
              },
            }}
            value={inc_of_tax}
            name={formField.name}
            disabled={formField.disable}
            control={
              <Checkbox
                checked={inc_of_tax}
                size="small"
                checkedIcon={
                  <CheckBoxOutlined
                    fontSize="small"
                    sx={{
                      color: "#BEBEBE",
                      fontSize: { sm: "1.8vw", md: "1.2vw" },
                    }}
                  />
                }
                icon={
                  <CheckBoxOutlineBlankOutlined
                    fontSize="small"
                    sx={{
                      color: "#BEBEBE",
                      fontSize: { sm: "1.8vw", md: "1.2vw" },
                    }}
                  />
                }
                sx={{ p: "0.5vw 0.3vw" }}
              />
            }
            label={t(
              `common_components.extra_person_popup.${formField.label
                .toLowerCase()
                .replace(/\s+/g, "_")}`
            )}
            labelPlacement="end"
          />
        );
      default:
        return null;
    }
  };

  const handleRemove = (index) => {
    if (isRemovePerson) {
      const person = getValues(`extra_persons.${index}`);
      if (person?.addon_id) {
        handleCheckoutPerson({
          ...person,
          actual_checkout_date:
            person.actual_checkout_date || format(new Date(), "yyyy-MM-dd HH:mm"),
        });
      }
      return;
    }
    remove(index);
  };

  const handleCheckoutPerson = async (person = personToCheckout) => {
    try {
      setIsSubmitted(true);
      if (
        person?.actual_checkout_date >
        getLocalTimeFromUtc(expected_checkout_date).dateTimeString
      ) {
        errorToster(
          t("common_components.extend_stay_popup.toast.extented_stay")
        );
        return;
      }

      const consolidatedSlabs = consolidateTaxRatesSlabs(taxRates);
      const taxObj = getTaxPercentForAmount(person.addon_price, consolidatedSlabs, inc_of_tax) || {};
      const rowTaxRate = Number(taxObj.rate || 0);

      const calculatedAmount = getBasePriceFromFomNetRent(
        Number(person.addon_price),
        inc_of_tax,
        rowTaxRate
      );

      const response = await api.post(END_POINT.ADDON.UPDATE_EXTRA_PERSON, {
        extra_persons: [
          {
            ...person,
            addon_price: calculatedAmount,
            tax_rate: rowTaxRate,
            ...getTaxBreakdown(taxObj),
          },
        ],
      });
      setConfirmationDlg(false);
      if (response.status === 200) {
        dispatch(setTriggered(!isTriggered));
        successToster(
          <span
            dangerouslySetInnerHTML={{
              __html: t(
                "common_components.extra_person_popup.toast.person_removed_success",
                { roomNo: room_no }
              ),
            }}
          />
        );
        setPersonToCheckout(null);
        handleCloseModal();
      }
    } catch (error) {
      errorToster(t("common_components.alert.something_went_wrong"));
    } finally {
      setIsSubmitted(false);
    }
  };

  const onSubmit = async (data) => {
    if (isRemovePerson) return;

    const addonData = {
      ...data,
      images: [...capturedImage, ...hiddenCamImage],
      taxRates: taxRates,
    };
    if (
      addonData?.extra_persons?.length >
        pax - (no_of_children + no_of_adults + no_of_extra_persons) &&
      !isRemovePerson
    ) {
      setFormData(addonData);
      setConfirmationLabel(
        `${t(
          "common_components.extra_person_popup.alert.maximum_occupancy_reached_for_this_room"
        )}.\n ${t(
          "common_components.extra_person_popup.alert.are_you_sure_you_want_to_add_a_person"
        )} ?`
      );
      setConfirmationDlg(true);
      return;
    } else {
      debouncedAddOrUpdatePerson(addonData);
    }
  };

  const addOrUpdatePerson = async (data) => {
    try {
      const {
        inc_of_tax,
        extra_persons,
        images,
        taxRates: currentTaxRates,
      } = data;
      const addon_checkin_date = format(new Date(), "yyyy-MM-dd HH:mm");

      const processedPersons = extra_persons.map((person) => {
        const amount = person.addon_price;
        const consolidatedSlabs = consolidateTaxRatesSlabs(currentTaxRates || taxRates);

        const taxObj = getTaxPercentForAmount(amount, consolidatedSlabs, inc_of_tax) || {};

        const rowTaxRate = Number(taxObj.rate || 0);

        const calculatedAmount = inc_of_tax
          ? getBasePriceFromFomNetRent(Number(amount), 1, rowTaxRate)
          : Number(amount);

        return {
          ...person,
          addon_price: Number(calculatedAmount),
          tax_rate: rowTaxRate,
          ...getTaxBreakdown(taxObj),
        };
      });

      const FormData = require("form-data");
      const formDataInstance = new FormData();
      formDataInstance.append("data", JSON.stringify({ mobile }));

      const imageUrls = images
        ?.filter((c) => typeof c === "string")
        .map((v) => ({ key: v }));

      if (imageUrls.length > 0) {
        formDataInstance.append(
          "addon_person_image",
          JSON.stringify(imageUrls)
        );
      }
      images?.forEach((file) => {
        if (typeof file !== "string") {
          formDataInstance.append("addon_person_image", file);
        }
      });
      if (
        addon_checkin_date >
          getLocalTimeFromUtc(expected_checkout_date).dateTimeString
      ) {
        errorToster(
          t(
            "common_components.extra_person_popup.alert.extend_stay_before_adding_the_person"
          )
        );
        return;
      }

      formDataInstance.append(
        "extra_persons",
        JSON.stringify({
          extra_persons: processedPersons,
          occupancy_key,
          addon_checkin_date,
          total_tax_rate: total_occupancy_tax_rate
        })
      );
      const res = await api.postFormData(
        END_POINT.ADDON.ADD_EXTRA_PERSON,
        formDataInstance
      );

      if (res?.status === 200) {
        successToster(
          <span
            dangerouslySetInnerHTML={{
              __html: t(
                "common_components.extra_person_popup.toast.person_added_success",
                { roomNo: room_no }
              ),
            }}
          />
        );
        dispatch(setTriggered(!isTriggered));
        handleCloseModal();
      }
    } catch (error) {
      errorToster(t("common_components.alert.something_went_wrong"));
    } finally {
      setIsSubmitted(false);
    }
  };

  const debouncedAddOrUpdatePerson = useMemo(
    () =>
      debounce((data) => {
        addOrUpdatePerson(data);
      }, 1000),
    [total_occupancy_tax_rate, taxRates]
  );

  return (
    <>
      <Box
        sx={{
          maxHeight: { sm: "60vw", md: "50vw" },
          overflowY: "auto !important",
          width: { xs: "88vw", sm: "60vw", md: "40vw" },
          padding: { xs: "0.8% 0 2% 0", md: 0 },
        }}
      >
        <Box
          sx={{
            width: "94%",
            margin: "0 auto 2% auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            sx={{
              fontSize: {
                xs: "4.78vw",
                sm: "3vw",
                fontWeight: "600",
                md: "1.389vw",
                color: "#252525",
              },
            }}
          >
            {modalName === "Add Person"
              ? t("common_components.extra_person_popup.add_person")
              : t("common_components.extra_person_popup.remove_person")}
          </Typography>

          <IconButton
            sx={{ m: "1% 0" }}
            size="small"
            onClick={() => setOpenCamera(true)}
            disabled={isRemovePerson}
          >
            <Badge
              sx={{
                "& .MuiBadge-badge": {
                  fontSize: { xs: 12, sm: "1.44vw", md: "0.83vw" },
                },
              }}
              showZero
              badgeContent={
                defaultCamImage?.length || capturedImage?.length || 0
              }
            >
              <Box
                sx={{
                  height: { xs: "5.38vw", sm: "2.512vw", md: "1.456vw" },
                  width: { xs: "7.18vw", sm: "3.36vw", md: "1.944vw" },
                }}
              >
                <img
                  src={IconConstants.Camera}
                  alt=""
                  style={{ height: "100%", width: "100%" }}
                />
              </Box>
            </Badge>
          </IconButton>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "start",
            alignItems: "center",
            gap: "0.694vw",
            width: "94%",
            margin: "1% auto",
          }}
        >
          <Box
            sx={{
              border: "0.5px solid #DEA829",
              borderRadius: "50%",
              width: {
                xs: "8vw",
                sm: "3.597vw",
                md: "2.5vw",
              },
              height: {
                xs: "8vw",
                sm: "3.597vw",
                md: "2.5vw",
              },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: {
                  xs: "2.56vw",
                  sm: "1.199vw",
                  md: "0.694vw",
                },
                color: "#252525",
                fontWeight: 600,
              }}
            >
              {data?.room_no || "-"}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: {
                xs: "4.667vw",
                sm: "2.5vw",
                fontWeight: 500,
                md: "1.111vw",
                color: "#DFA928",
              },
            }}
          >
            {data?.name}
          </Typography>
        </Box>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "20vw" }}>
            <CircularProgress size={40} sx={{ color: primaryColor }} />
          </Box>
        ) : extra_persons.length > 0 ? (
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "space-between",
                width: { sm: "69%", md: "75%" },
                margin: "0 auto",
              }}
            >
              {fields.map((_, countInd) => (
                <Fragment key={countInd}>
                  {formFields(countInd).loopFields.map(
                    (formField, fieldIndex) => (
                      <FormControl
                        key={fieldIndex}
                        sx={{
                          width:
                            isRemovePerson &&
                            formField.name === `extra_persons.${countInd}.addon_price`
                              ? { xs: "calc(100% - 36px)", sm: "49%" }
                              : formField?.width,
                          marginTop: "4.5%",
                        }}
                      >
                        {formField.name ===
                          `extra_persons.${countInd}.addon_name` && (
                          <Box
                            sx={{
                              position: "absolute",
                              left: "-18%",
                              top: "20%",
                              borderRadius: "1.389vw",
                              backgroundColor: primaryColor,
                              height: { sm: "2.111vw", md: "1.806vw" },
                              width: { sm: "2.111vw", md: "1.806vw" },
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: {
                                  xs: "4.1vw",
                                  sm: "1.5vw",
                                  md: "0.764vw",
                                  fontWeight: 700,
                                  color: contrastText,
                                },
                              }}
                            >
                              {countInd + 1}
                            </Typography>
                          </Box>
                        )}
                        <Controller
                          {...methods}
                          name={formField.name}
                          defaultValue=""
                          render={({ field, fieldState: { error } }) => {
                            return renderField(formField, field, error);
                          }}
                        />
                        {formField.name ===
                          `extra_persons.${countInd}.addon_price` && (
                          <Box
                            sx={{
                              position: "absolute",
                              right:
                                {
                                  xs: isRemovePerson ? "-36px" : "-10%",
                                  sm:
                                    countInd === fields.length - 1 &&
                                    fields.length !== 1 &&
                                    fields.length < 10 &&
                                    modalName === "Add Person"
                                      ? "-36%"
                                      : "-18%",
                                },
                              top:
                                {
                                  xs: isRemovePerson ? "50%" : "12%",
                                  sm:
                                    countInd === fields.length - 1 &&
                                    fields.length < 10 &&
                                    modalName === "Add Person"
                                      ? "10%"
                                      : "5%",
                                },
                              transform: { xs: isRemovePerson ? "translateY(-50%)" : "none", sm: "none" },
                              display: "flex",
                              alignItems: "center",
                              zIndex: 2,
                            }}
                          >
                            {countInd === fields.length - 1 &&
                              fields.length < 10 &&
                              modalName === "Add Person" && (
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    append({ addon_price: extraAdultPrice });
                                  }}
                                  disabled={
                                    hasEmptyFields(
                                      !!extra_persons
                                        ? extra_persons[fields.length - 1]
                                        : {}
                                    ) || fields.length > 4
                                  }
                                >
                                  <AddCircleOutline
                                    fontSize="small"
                                    sx={{
                                      color: hasEmptyFields(
                                        !!extra_persons
                                          ? extra_persons[fields.length - 1]
                                          : {}
                                      )
                                        ? "gray"
                                        : "#E2A81F",
                                      fontSize: {
                                        sm: "2.389vw",
                                        md: "1.806vw",
                                      },
                                    }}
                                  />
                                </IconButton>
                              )}
                            {(fields.length > 1 || isRemovePerson) && (
                              <IconButton
                                onClick={() => handleRemove(countInd)}
                                size="small"
                              >
                                <RemoveCircleTwoTone
                                  fontSize="small"
                                  sx={{
                                    color: "#FF5A3E",
                                    fontSize: { xs: "6vw", sm: "2.389vw", md: "1.806vw" },
                                  }}
                                />
                              </IconButton>
                            )}
                          </Box>
                        )}
                      </FormControl>
                    )
                  )}
                </Fragment>
              ))}
              {formFields().commonFields.map((formField, fieldIndex) => (
                <FormControl key={fieldIndex} sx={{ width: formField?.width }}>
                  <Controller
                    {...methods}
                    name={formField.name}
                    defaultValue=""
                    render={({ field, fieldState: { error } }) => {
                      return renderField(formField, field, error);
                    }}
                  />
                </FormControl>
              ))}
              <Button
                onClick={handleCloseModal}
                variant="outlined"
                sx={{
                  width: isRemovePerson ? "100%" : "49%",
                  fontSize: { xs: "3.08vw", sm: "1.333vw", md: "1.111vw" },
                  fontWeight: 700,
                  marginTop: "5%",
                }}
              >
                {t("common_components.extra_person_popup.cancel")}
              </Button>
              {!isRemovePerson && (
                <Can feature={FEATURES.ADDONS} action={ACTIONS.CREATE}>
                  <Button
                    variant="contained"
                    disabled={isSubmitted}
                    type="submit"
                    sx={{
                      width: "49%",
                      fontSize: { xs: "3.08vw", sm: "1.333vw", md: "1.111vw" },
                      fontWeight: 700,
                      marginTop: "5%",
                    }}
                  >
                    {t("common_components.extra_person_popup.confirm")}
                  </Button>
                </Can>
              )}
            </Box>
          </form>
        ) : (
          <Box sx={{ textAlign: "center", width: 1, margin: "5% 0" }}>
            <Typography
              sx={{
                fontSize: {
                  xs: "3.08vw",
                  sm: "1.44vw",
                  md: "0.83vw",
                  color: "#989898",
                },
              }}
            >
              No persons to remove
            </Typography>
            <Button
              onClick={handleCloseModal}
              variant="outlined"
              sx={{
                width: { sm: "69%", md: "75%" },
                fontSize: { xs: "3.08vw", sm: "1.333vw", md: "1.111vw" },
                fontWeight: 700,
                marginTop: "5%",
              }}
            >
              {t("common_components.extra_person_popup.cancel")}
            </Button>
          </Box>
        )}
      </Box>
      <ConfirmationDlgBox
        open={confirmationDlg}
        isButtonDisabled={isSubmitted}
        confirmFunction={() => {
          if (personToCheckout) {
            handleCheckoutPerson();
            return;
          }
          setConfirmationDlg(false);
          if (formData) debouncedAddOrUpdatePerson(formData);
        }}
        setOpen={setConfirmationDlg}
        cancelFunction={() => setPersonToCheckout(null)}
        label={confirmationLabel}
      />
      <ReactWebcam
        openWebcam={openCamera}
        getCapturedImage={(data) => {
          setCapturedImage(data);
          setDefaultCamImage(null);
        }}
        closeWebcam={() => {
          setOpenCamera(!openCamera);
        }}
        defaultCamImage={defaultCamImage}
        imageName={"addon_person_image"}
      />
    </>
  );
};

export default AddOrRemovePerson;
